import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkle, Wallet, ChatCircleDots, Coin, MoonStars, X } from "@phosphor-icons/react";

/**
 * Celebratory pop-up shown once, right after a brand-new sign-up. Reads its
 * trigger from sessionStorage (set by AuthCallback on `is_new_user: true`).
 * Clears the flag on dismiss so it never shows again for the same session.
 */
export default function WelcomeBonusModal() {
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("ak_welcome_bonus");
      if (!raw) return;
      const parsed = JSON.parse(raw);
      // Show only if flag was set in the last 10 minutes
      if (!parsed || Date.now() - (parsed.at || 0) > 10 * 60 * 1000) {
        sessionStorage.removeItem("ak_welcome_bonus");
        return;
      }
      setInfo(parsed);
    } catch (_) { /* ignore */ }
  }, []);

  const close = () => {
    sessionStorage.removeItem("ak_welcome_bonus");
    setInfo(null);
  };

  if (!info) return null;

  const totalCredit = (info.credit || 100) + 5; // 100 welcome + 5 day-1 streak bonus

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[80] flex items-center justify-center p-4 anim-fadeup"
      onClick={close}
      data-testid="welcome-bonus-modal"
    >
      <div
        className="card-surface rounded-md max-w-md w-full p-6 sm:p-8 relative bg-gradient-to-br from-[#FFF6E0] to-[#FFE9B5] border-2 border-[#D4AF37]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={close}
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-black/10"
          aria-label="Close"
          data-testid="welcome-bonus-close"
        >
          <X size={18} />
        </button>

        <div className="flex items-center gap-2 text-[#7C2D12] label-eyebrow">
          <Sparkle size={14} weight="fill" /> Welcome to AstroKuber
        </div>
        <p className="font-serif-display text-3xl sm:text-4xl mt-3 font-light text-[#3F2D1B] leading-tight">
          ₹{totalCredit} credited <span className="italic text-gold">to your wallet</span>
        </p>
        <p className="text-[#3F2D1B] text-sm mt-3 font-light leading-relaxed">
          A warm welcome from us — your wallet is loaded with <b>₹{info.credit || 100} welcome bonus</b> + <b>₹5 daily-login streak bonus</b>. Use it to start a reading with any of our AI astrology experts (your first 2 minutes with them are free too).
        </p>

        <div className="grid grid-cols-3 gap-3 mt-5">
          <div className="card-surface rounded-md p-3 text-center bg-white/60">
            <Wallet size={20} weight="duotone" className="text-gold mx-auto" />
            <div className="font-mono text-lg font-bold text-[#3F2D1B] mt-1">₹{totalCredit}</div>
            <div className="text-[10px] text-muted mt-0.5">In your wallet</div>
          </div>
          <div className="card-surface rounded-md p-3 text-center bg-white/60">
            <ChatCircleDots size={20} weight="duotone" className="text-gold mx-auto" />
            <div className="font-mono text-lg font-bold text-[#3F2D1B] mt-1">2 min</div>
            <div className="text-[10px] text-muted mt-0.5">Free AI chat</div>
          </div>
          <div className="card-surface rounded-md p-3 text-center bg-white/60">
            <Coin size={20} weight="duotone" className="text-gold mx-auto" />
            <div className="font-mono text-lg font-bold text-[#3F2D1B] mt-1">₹5/day</div>
            <div className="text-[10px] text-muted mt-0.5">Streak bonus</div>
          </div>
        </div>

        <button
          onClick={() => { close(); navigate("/astrologers"); }}
          className="btn-primary rounded-full w-full mt-6 py-3 font-medium flex items-center justify-center gap-2"
          data-testid="welcome-bonus-cta"
        >
          <ChatCircleDots size={16} weight="duotone" />
          Find your astrologer
        </button>
        <button
          onClick={() => { close(); navigate("/kundali-calculator"); }}
          className="btn-ghost rounded-full w-full mt-2 py-2.5 text-sm font-medium flex items-center justify-center gap-2"
          data-testid="welcome-bonus-kundali"
        >
          <MoonStars size={14} weight="duotone" /> Get my free kundali first
        </button>
        <p className="text-[#7C2D12]/70 text-[11px] mt-3 text-center font-light">
          Open the app daily to earn extra streak bonuses — up to ₹25/day on day 7.
        </p>
      </div>
    </div>
  );
}
